// Revisionszustand der Farb-/Dekor-/Texturkataloge im Browser
// (docs/architecture/color-catalog-revisions.md, F2). Die Codedateien bleiben
// Revision 1; hier liegen nur Abweichungen aus dem Backend, bereits geladene
// ältere Revisionen und der Bindungskontext des geöffneten Auftrags.
// Kein Zugriff auf App-State: der Core injiziert den Kontext.

const MAX_BINDINGS = 5000;
const BINDING_KEY_PATTERN = /^[a-z][a-z0-9_-]{0,79}:.{1,80}$/;

let currentState = null;
let currentChanges = new Map();
// Verwaltungsfelder (F6): null = unbekannt (Backend nicht erreichbar).
let currentFlags = null;
const stateSnapshots = new Map();
const revisionEntries = new Map();
const pendingRequests = new Map();
const contextOverrides = [];
let contextProvider = null;
let dataLoadedListener = null;
let changeVersion = 0;

function plainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function stateNumber(value) {
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

// Liefert einen bereinigten, eingefrorenen Kontext oder null (kein Auftrag).
export function sanitizeColorBinding(value) {
  if (!plainObject(value)) return null;
  const bindings = {};
  let count = 0;
  if (plainObject(value.bindings)) {
    for (const [key, revision] of Object.entries(value.bindings)) {
      if (count >= MAX_BINDINGS) break;
      if (!BINDING_KEY_PATTERN.test(key) || !Number.isSafeInteger(revision) || revision < 1) continue;
      bindings[key] = revision;
      count++;
    }
  }
  return Object.freeze({ state: stateNumber(value.state), bindings: Object.freeze(bindings) });
}

// Auftrag ohne gespeicherte Bindung = Erstbestand (Revision 1).
const LEGACY_CONTEXT = Object.freeze({ state: null, bindings: Object.freeze({}) });

export function configureColorBindingContext(provider) {
  contextProvider = typeof provider === 'function' ? provider : null;
}

// Für Abläufe, die vorübergehend ein anderes Projektobjekt aktiv setzen.
export function withColorBindingContext(context, callback) {
  contextOverrides.push(context === null ? null : (sanitizeColorBinding(context) || LEGACY_CONTEXT));
  try {
    return callback();
  } finally {
    contextOverrides.pop();
  }
}

// null = aktueller Katalogstand (kein Auftragsbezug, z. B. Adminansichten).
export function activeColorBindingContext() {
  if (contextOverrides.length) return contextOverrides[contextOverrides.length - 1];
  if (!contextProvider) return null;
  const provided = contextProvider();
  if (provided === null || provided === undefined) return null;
  if (provided === false) return LEGACY_CONTEXT;
  return sanitizeColorBinding(provided) || LEGACY_CONTEXT;
}

export function colorCatalogState() {
  return currentState;
}

export function colorRevisionChangeVersion() {
  return changeVersion;
}

function entryMap(entries) {
  const map = new Map();
  for (const item of Array.isArray(entries) ? entries : []) {
    if (!plainObject(item) || typeof item.catalog !== 'string' || typeof item.code !== 'string'
      || !Number.isSafeInteger(item.revision) || item.revision < 1 || !plainObject(item.entry)) continue;
    const key = `${item.catalog}:${item.code}`;
    const entry = Object.freeze({ ...item.entry });
    revisionEntries.set(`${key}@${item.revision}`, entry);
    map.set(key, { revision: item.revision, entry });
  }
  return map;
}

function flagKeys(values) {
  return Array.isArray(values)
    ? values.filter(key => typeof key === 'string' && BINDING_KEY_PATTERN.test(key))
    : [];
}

function sanitizeFlags(value) {
  if (!plainObject(value)) return null;
  return Object.freeze({
    standard: Object.freeze([...new Set(flagKeys(value.standard))]),
    inactive: new Set(flagKeys(value.inactive))
  });
}

function catalogOf(key) {
  return key.slice(0, key.indexOf(':'));
}

// Übernimmt Katalogstand, aktuelle Abweichungen und Verwaltungsfelder;
// liefert die betroffenen Kataloge.
export function applyColorCatalogState(payload) {
  if (!plainObject(payload) || stateNumber(payload.state) === null || !Array.isArray(payload.entries)) return null;
  const next = entryMap(payload.entries);
  const nextFlags = sanitizeFlags(payload.flags);
  const touched = new Set([...currentChanges.keys(), ...next.keys()].map(catalogOf));
  for (const flags of [currentFlags, nextFlags]) {
    if (!flags) continue;
    flags.standard.forEach(key => touched.add(catalogOf(key)));
    flags.inactive.forEach(key => touched.add(catalogOf(key)));
  }
  currentState = payload.state;
  currentChanges = next;
  currentFlags = nextFlags;
  changeVersion++;
  return [...touched];
}

// Schlüssel `katalog:code` mit Haken „Standard“ in Reihenfolge der Administration,
// oder null, solange der Katalogstand unbekannt ist.
export function colorStandardKeys() {
  return currentFlags ? currentFlags.standard : null;
}

// Haken „Auswählbar“ entfernt: nicht in Listen, gespeicherte Werte bleiben lesbar.
export function isColorEntryInactive(key) {
  return Boolean(currentFlags?.inactive.has(key));
}

export function currentColorRevision(key) {
  return currentChanges.get(key) || null;
}

export function currentColorChanges() {
  return currentChanges;
}

export function colorStateSnapshot(at) {
  return stateSnapshots.get(at) || null;
}

export function colorRevisionEntry(key, revision) {
  return revisionEntries.get(`${key}@${revision}`) || null;
}

let colorSelectedListener = null;

// Der Core registriert hier die Bindung aktiver Auswahlen an den Auftrag;
// der Colorpicker meldet jede aktive Auswahl über notifyColorSelected.
export function configureColorSelectionListener(listener) {
  colorSelectedListener = typeof listener === 'function' ? listener : null;
}

export function notifyColorSelected(value) {
  try {
    colorSelectedListener?.(value);
  } catch (error) {
    console.error('Farbauswahl konnte nicht an den Auftrag gebunden werden.', error);
  }
}

export function configureColorRevisionDataListener(listener) {
  dataLoadedListener = typeof listener === 'function' ? listener : null;
}

async function readJson(path) {
  const response = await fetch(path, { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error('Farbkatalog-Revisionen nicht verfügbar.');
  const payload = await response.json();
  if (!payload?.ok) throw new Error('Ungültige Antwort der Farbkatalog-Revisionen.');
  return payload;
}

function dedupe(key, work) {
  if (!pendingRequests.has(key)) {
    const request = Promise.resolve().then(work).finally(() => pendingRequests.delete(key));
    pendingRequests.set(key, request);
  }
  return pendingRequests.get(key);
}

export function loadColorStateSnapshot(at) {
  const state = stateNumber(at);
  if (state === null) return Promise.resolve(null);
  if (stateSnapshots.has(state)) return Promise.resolve(stateSnapshots.get(state));
  return dedupe(`state:${state}`, async () => {
    const payload = await readJson(`/api/color-catalogs/state?at=${state}`);
    if (!Array.isArray(payload.entries) || payload.at !== state) throw new Error('Ungültiger Katalogstand.');
    const snapshot = entryMap(payload.entries);
    stateSnapshots.set(state, snapshot);
    changeVersion++;
    return snapshot;
  });
}

export function loadColorRevisions(refs) {
  const wanted = [...new Set(refs)].filter(ref => !revisionEntries.has(ref));
  if (!wanted.length) return Promise.resolve([]);
  return dedupe(`refs:${wanted.join('|')}`, async () => {
    const results = [];
    for (let index = 0; index < wanted.length; index += 200) {
      const query = wanted.slice(index, index + 200).map(ref => `ref=${encodeURIComponent(ref)}`).join('&');
      const payload = await readJson(`/api/color-catalogs/revisions?${query}`);
      entryMap(payload.revisions);
      results.push(...(payload.revisions || []));
    }
    changeVersion++;
    return results;
  });
}

// Lädt im Hintergrund nach und meldet dem Core, dass neu gezeichnet werden soll.
export function requestColorRevisionData({ at = null, refs = [] } = {}) {
  const jobs = [];
  if (stateNumber(at) !== null && !stateSnapshots.has(at)) jobs.push(loadColorStateSnapshot(at));
  if (refs.length) jobs.push(loadColorRevisions(refs));
  if (!jobs.length) return;
  Promise.all(jobs).then(() => dataLoadedListener?.()).catch(error => {
    console.warn('Farbkatalog-Revisionen konnten nicht nachgeladen werden.', error);
  });
}

// Nur für Tests.
export function resetColorRevisionStateForTests() {
  currentState = null;
  currentChanges = new Map();
  currentFlags = null;
  stateSnapshots.clear();
  revisionEntries.clear();
  pendingRequests.clear();
  contextOverrides.length = 0;
  contextProvider = null;
  dataLoadedListener = null;
  colorSelectedListener = null;
  changeVersion++;
}
